import { useState } from "react"
import { X, Zap, Users } from "lucide-react"
import { useNavigate, useParams } from "react-router-dom"
import ProfileSidebar from "./ProfileSideBar"

// ── AI illustration ───────────────────────────────────────────────────────────
function AiIllustration() {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 160 96"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {[0, 1, 2, 3].map((i) => (
        <line
          key={`h${i}`}
          x1="0"
          y1={24 * i}
          x2="160"
          y2={24 * i}
          stroke="#f4f4f5"
          strokeWidth="1"
        />
      ))}
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <line
          key={`v${i}`}
          x1={26.6 * i}
          y1="0"
          x2={26.6 * i}
          y2="96"
          stroke="#f4f4f5"
          strokeWidth="1"
        />
      ))}
      <rect x="56" y="28" width="48" height="52" fill="#e4e4e7" />
      <rect x="64" y="12" width="32" height="28" fill="#d4d4d8" />
      <rect x="70" y="20" width="6" height="6" fill="#71717a" />
      <rect x="84" y="20" width="6" height="6" fill="#71717a" />
      <rect x="56" y="28" width="48" height="2" fill="#a78bfa" opacity="0.6">
        <animate
          attributeName="y"
          values="28;76;28"
          dur="2.4s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="opacity"
          values="0.6;0.2;0.6"
          dur="2.4s"
          repeatCount="indefinite"
        />
      </rect>
      <path
        d="M46 18 L46 8 L56 8"
        stroke="#a78bfa"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M114 18 L114 8 L104 8"
        stroke="#a78bfa"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M46 78 L46 88 L56 88"
        stroke="#a78bfa"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M114 78 L114 88 L104 88"
        stroke="#a78bfa"
        strokeWidth="1.5"
        fill="none"
      />
      <circle cx="24" cy="48" r="3" fill="#a78bfa" opacity="0.4">
        <animate
          attributeName="opacity"
          values="0.4;1;0.4"
          dur="1.6s"
          repeatCount="indefinite"
        />
      </circle>
      <circle cx="136" cy="48" r="3" fill="#a78bfa" opacity="0.4">
        <animate
          attributeName="opacity"
          values="0.4;1;0.4"
          dur="1.6s"
          begin="0.4s"
          repeatCount="indefinite"
        />
      </circle>
      <rect
        x="62"
        y="80"
        width="36"
        height="10"
        fill="#7c3aed"
        opacity="0.15"
      />
      <text
        x="80"
        y="88"
        textAnchor="middle"
        fontSize="6"
        fill="#7c3aed"
        fontFamily="IBM Plex Mono"
        fontWeight="600"
        letterSpacing="1"
      >
        AI
      </text>
    </svg>
  )
}

// ── Human illustration ────────────────────────────────────────────────────────
function HumanIllustration() {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 160 96"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {[0, 1, 2, 3].map((i) => (
        <line
          key={`h${i}`}
          x1="0"
          y1={24 * i}
          x2="160"
          y2={24 * i}
          stroke="#f4f4f5"
          strokeWidth="1"
        />
      ))}
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <line
          key={`v${i}`}
          x1={26.6 * i}
          y1="0"
          x2={26.6 * i}
          y2="96"
          stroke="#f4f4f5"
          strokeWidth="1"
        />
      ))}
      <rect x="28" y="36" width="32" height="40" fill="#e4e4e7" />
      <rect x="34" y="18" width="20" height="24" fill="#d4d4d8" />
      <rect x="100" y="36" width="32" height="40" fill="#d4d4d8" />
      <rect x="106" y="18" width="20" height="24" fill="#a1a1aa" />
      <rect x="28" y="6" width="36" height="10" fill="#3b82f6" opacity="0.15" />
      <text
        x="46"
        y="13.5"
        textAnchor="middle"
        fontSize="5.5"
        fill="#1d4ed8"
        fontFamily="IBM Plex Mono"
        fontWeight="600"
        letterSpacing="0.5"
      >
        QUESTION
      </text>
      <rect x="96" y="6" width="40" height="10" fill="#3b82f6" opacity="0.1" />
      <text
        x="116"
        y="13.5"
        textAnchor="middle"
        fontSize="5.5"
        fill="#1d4ed8"
        fontFamily="IBM Plex Mono"
        fontWeight="600"
        letterSpacing="0.5"
      >
        AI HINT ✦
      </text>
      <line
        x1="64"
        y1="48"
        x2="96"
        y2="48"
        stroke="#93c5fd"
        strokeWidth="1"
        strokeDasharray="4 3"
      >
        <animate
          attributeName="stroke-dashoffset"
          values="0;-14"
          dur="1s"
          repeatCount="indefinite"
        />
      </line>
      <circle cx="80" cy="48" r="3" fill="#3b82f6" opacity="0.5">
        <animate
          attributeName="opacity"
          values="0.5;1;0.5"
          dur="1.2s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="r"
          values="3;5;3"
          dur="1.2s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  )
}

// ── Page component ────────────────────────────────────────────────────────────
// Route: /jobs/:orgId/:jobId/add-structure-wrapper
const AddRoundPanelPage = () => {
  const { orgId, jobId } = useParams()
  const navigate = useNavigate()
  const [hovered, setHovered] = useState(null)

  // These could also come from route state or an API call if needed
  const roundNumber = 1
  const jobName = "Job"

  const handleClose = () => {
    navigate("/jobs")
  }

  const handleSelect = (type) => {
    if (type === "ai") {
      navigate(`/jobs/${orgId}/${jobId}/add-ai-interview`)
      return
    }
    if (type === "human") {
      navigate(`/jobs/${orgId}/${jobId}/add-structure`)
      return
    }
  }

  return (
    <div
      className="flex min-h-screen bg-zinc-50"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <style>{`
      @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
    `}</style>

      {/* Sidebar */}
      <div className="border-r border-zinc-200 h-full bg-white overflow-y-auto min-h-screen">
        <ProfileSidebar activeTab="jobs" setActiveTab={() => {}} />
      </div>

      {/* Main content */}
      <div
        className="flex-1 ml-60 flex flex-col overflow-hidden"
        style={{ height: "100dvh" }}
      >
        {/* ── Top bar ── */}
        <div className="bg-white px-8 py-4 flex items-center justify-between border-b border-zinc-200 shrink-0">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 bg-zinc-900 flex items-center justify-center flex-shrink-0"
              style={{ borderRadius: 0 }}
            >
              <span
                className="text-white font-bold text-xs"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
               *
              </span>
            </div>
            <div>
              <p className="text-sm font-bold uppercase tracking-wide text-zinc-900">
                Add Round Info
              </p>
              <p className="text-xs text-zinc-400 mt-0.5">---------</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-7 h-7 border border-zinc-200 bg-white hover:border-zinc-400 hover:bg-zinc-50 transition-all text-zinc-400 hover:text-zinc-700"
            style={{ borderRadius: 0 }}
          >
            <X size={13} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 flex flex-col items-center pt-32 px-8 overflow-y-auto">
          {/* Prompt */}
          <div className="mb-10 text-center">
            <p
              className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              · Interview Type
            </p>
            <h2 className="text-lg font-bold text-zinc-900">
              What kind of interview you want to setup?
            </h2>
          </div>

          {/* Cards */}
          <div className="flex gap-4 flex-wrap justify-center">
            {[
              {
                id: "ai",
                label: "AI Interview",
                tag: "AUTOMATED",
                tagColor: "text-violet-700 bg-violet-50 border-violet-200",
                description:
                  "An AI avatar conducts the full interview autonomously",
                Icon: Zap,
                IllustrationEl: AiIllustration,
              },
              {
                id: "human",
                label: "Human Interview",
                tag: "HUMAN + AI COPILOT",
                tagColor: "text-blue-700 bg-blue-50 border-blue-200",
                description:
                  "A human interviewer guided by real-time AI suggestions",
                Icon: Users,
                IllustrationEl: HumanIllustration,
              },
            ].map(
              ({
                id,
                label,
                tag,
                tagColor,
                description,
                Icon,
                IllustrationEl,
              }) => {
                const isHovered = hovered === id
                return (
                  <button
                    key={id}
                    onClick={() => handleSelect(id)}
                    onMouseEnter={() => setHovered(id)}
                    onMouseLeave={() => setHovered(null)}
                    className={`flex flex-col text-left border transition-all duration-150 overflow-hidden ${
                      isHovered
                        ? "border-zinc-900 bg-zinc-900 shadow-lg"
                        : "border-zinc-200 bg-white hover:border-zinc-400"
                    }`}
                    style={{ borderRadius: 0, width: 220 }}
                  >
                    {/* Illustration area */}
                    <div
                      className={`w-full border-b transition-colors duration-150 ${
                        isHovered
                          ? "border-zinc-700 bg-zinc-800"
                          : "border-zinc-100 bg-zinc-50"
                      }`}
                      style={{ height: 110 }}
                    >
                      <IllustrationEl />
                    </div>

                    {/* Content */}
                    <div className="px-4 py-4 flex flex-col gap-2">
                      <span
                        className={`self-start inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                          isHovered
                            ? "border-zinc-600 bg-zinc-700 text-zinc-300"
                            : tagColor
                        }`}
                        style={{ borderRadius: 0 }}
                      >
                        <Icon size={9} />
                        {tag}
                      </span>

                      <p
                        className={`text-xs font-bold uppercase tracking-wide transition-colors duration-150 ${
                          isHovered ? "text-white" : "text-zinc-900"
                        }`}
                      >
                        {label}
                      </p>

                      <p
                        className={`text-[11px] leading-relaxed transition-colors duration-150 ${
                          isHovered ? "text-zinc-400" : "text-zinc-400"
                        }`}
                      >
                        {description}
                      </p>

                      {/* CTA row */}
                      <div
                        className={`flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                          isHovered ? "text-zinc-300" : "text-zinc-300"
                        }`}
                        style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                      >
                        Select
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </button>
                )
              },
            )}
          </div>

          {/* Footer hint */}
          <p
            className="mt-10 text-[10px] text-zinc-300 uppercase tracking-widest font-bold"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            You can add more rounds after setup
          </p>
        </div>
      </div>
    </div>
  )
}

export default AddRoundPanelPage
